import React from "react";
import { useIntl } from "react-intl";
import { Box, Button, Chip, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { hasMasterValue, differsFromOrganization } from "../model/overrideModel";
import { compactInputSx, inputSx, ui } from "../styles";

export const CustomerLogo = ({ fallbackIconSize, size, src }) => (
  <Box sx={{ width:size, height:size, borderRadius:1.15, display:"grid", placeItems:"center", flexShrink:0,
    bgcolor:src ? "#fff" : theme => alpha(theme.palette.primary.main,.075), color:"primary.main",
    border:src ? `1px solid ${ui.border}` : "none", overflow:"hidden" }}>
    {src ? <Box component="img" src={src} alt="" sx={{ display:"block", width:"100%", height:"100%", p:.45, boxSizing:"border-box", objectFit:"contain" }}/>
      : <BusinessOutlinedIcon sx={{ fontSize:fallbackIconSize }}/>} 
  </Box>
);

export const SectionHeader = ({ title, description }) => (
  <Box sx={{ mb:1.15 }}>
    <Typography sx={{ color:ui.text, fontSize:14, fontWeight:600, letterSpacing:"-.008em" }}>{title}</Typography>
    {description && <Typography sx={{ mt:.22, color:ui.muted, fontSize:10.3, lineHeight:1.45 }}>{description}</Typography>}
  </Box>
);

export const FieldGroupTitle = ({ children }) => (
  <Typography sx={{ mb:.7, color:ui.muted, fontSize:9.5, lineHeight:1.2, fontWeight:650, textTransform:"uppercase", letterSpacing:".055em" }}>{children}</Typography>
);

export const OverrideCountChip = ({ count, label }) => {
  const intl = useIntl();
  if (!count) return null;
  return <Chip size="small" label={label || intl.formatMessage({ id:"admin.crm.customerCard.changes" },{ count })}
    sx={{ height:18, bgcolor:"#fff7ed", color:"#b54708", border:"1px solid #fed7aa", fontSize:8.5, fontWeight:600, "& .MuiChip-label":{ px:.55 } }}/>;
};

export const NewOrganizationChip = () => {
  const intl = useIntl();
  return <Chip size="small" label={intl.formatMessage({ id:"admin.crm.customerCard.organizationLink.new" })} sx={{ height:18, borderRadius:.9, bgcolor:"#fff2e2", color:"#b54708", border:"1px solid #fed7aa",
    fontSize:8.7, fontWeight:700, letterSpacing:".035em", "& .MuiChip-label":{ px:.55 } }}/>;
};

export const InlineOverrideWarning = ({ children }) => (
  <Stack direction="row" spacing={.45} alignItems="center" sx={{ ml:2.8, mt:.25 }}>
    <WarningAmberRoundedIcon sx={{ color:"#b54708", fontSize:11 }}/>
    <Typography sx={{ color:"#b54708", fontSize:8.9, fontWeight:550 }}>{children}</Typography>
  </Stack>
);

export const HiddenFieldsToggle = ({ count, onClick, showHidden, warning=false }) => {
  const intl = useIntl();
  const label = warning
    ? intl.formatMessage({ id:"admin.crm.customerCard.addresses.hidden.showAndEdit" })
    : showHidden
      ? intl.formatMessage({ id:"admin.crm.customerCard.addresses.hidden.hide" })
      : intl.formatMessage({ id:"admin.crm.customerCard.addresses.hidden.count" },{ count });
  return <Button size="small" onClick={onClick} sx={warning
    ? { minHeight:22, px:.4, color:"#9a3412", textTransform:"none", fontSize:8.9, fontWeight:650 }
    : { minHeight:21, px:.35, color:ui.muted, textTransform:"none", fontSize:8.8 }}>{label}</Button>;
};

export const OverrideStatus = ({ changed, masterValue, onReset, compact=false, label }) => {
  const intl = useIntl();
  if (!changed) return null;
  const statusLabel = label || intl.formatMessage({ id:"admin.crm.customerCard.override.changedFromOrganization" });
  const title = hasMasterValue(masterValue)
    ? intl.formatMessage({ id:"admin.crm.customerCard.override.organizationValue" },{ value:masterValue })
    : intl.formatMessage({ id:"admin.crm.customerCard.override.differsFromOrganization" });
  return (
    <Stack component="span" direction="row" spacing={.4} alignItems="center" sx={{ minHeight:compact ? 14 : 16 }}>
      <WarningAmberRoundedIcon sx={{ color:"#b54708", fontSize:compact ? 11 : 12, flexShrink:0 }}/>
      <Tooltip title={title}><Box component="span" sx={{ color:"#b54708", fontSize:compact ? 8.8 : 9.2, fontWeight:550, cursor:"help" }}>{statusLabel}</Box></Tooltip>
      {onReset && <Button size="small" startIcon={<RestartAltRoundedIcon sx={{ fontSize:"12px !important" }}/>} onClick={event => { event.preventDefault(); onReset(); }}
        sx={{ minWidth:0, minHeight:18, ml:"auto !important", px:.25, py:0, color:ui.muted, textTransform:"none", fontSize:8.8, fontWeight:550 }}>{intl.formatMessage({ id:"admin.crm.customerCard.actions.reset" })}</Button>}
    </Stack>
  );
};

export const OptionItems = ({ options }) => {
  const intl = useIntl();
  return options.map(([value,messageId]) => <MenuItem key={value || "EMPTY"} value={value}>{messageId ? intl.formatMessage({ id:messageId }) : value}</MenuItem>);
};

export const MasterField = ({ children, compact=false, field, label, masterValue, onChange, onReset, required, select, type, value }) => {
  const changed = differsFromOrganization(value, masterValue);
  return (
    <TextField fullWidth select={select} type={type} label={label} value={value ?? ""} onChange={onChange} required={required}
      inputProps={type === "number" ? { min:0 } : undefined} sx={compact ? compactInputSx : inputSx}
      helperText={<OverrideStatus changed={changed} masterValue={masterValue} onReset={changed ? () => onReset(field, masterValue) : null}/>} 
      FormHelperTextProps={{ component:"div", sx:{ minHeight:changed ? 17 : 0, ...(compact ? {} : { mb:changed ? 0 : -.1 }) } }}>
      {children}
    </TextField>
  );
};
